import { Bug, Inbox, MessageSquareText, Send } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type SupportInquiry, type SupportInquiryCategory } from "../api";
import { BlockEditor, BlockRenderer, type DocumentBlock, paragraph } from "./block-editor";
import { Loading, ModalFrame, RequirementHint } from "./ui";

const categories: SupportInquiryCategory[] = ["BUG_REPORT", "FEATURE_REQUEST", "USAGE_QUESTION", "OTHER"];

export function SupportInquiryModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<"write" | "history">("write");
  const [category, setCategory] = useState<SupportInquiryCategory>("BUG_REPORT");
  const [subject, setSubject] = useState("");
  const [blocks, setBlocks] = useState<DocumentBlock[]>([paragraph()]);
  const [items, setItems] = useState<SupportInquiry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState("");
  const hasContent = blocks.some((block) => block.type === "IMAGE" || block.type === "TABLE" || (block.type === "PARAGRAPH" && block.text.trim()));
  const missing = [!subject.trim() ? t("support.subject") : "", !hasContent ? t("support.content") : ""].filter(Boolean);

  useEffect(() => {
    let active = true;
    void api.mySupportInquiries().then((page) => {
      if (!active) return;
      setItems(page.items);
      setNextCursor(page.nextCursor || null);
    }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : t("support.loadError"));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [t]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    if (!subject.trim() || !hasContent) return;
    const content = blocks.map((block) => {
      if (block.type === "PARAGRAPH") return block.text;
      if (block.type === "TABLE") return [block.columns.join("\t"), ...block.rows.map((row) => row.join("\t"))].join("\n");
      return block.caption || block.file.originalName;
    }).filter(Boolean).join("\n");
    setBusy(true); setError("");
    try {
      const created = await api.createSupportInquiry({ category, subject, content, blocks });
      setItems((current) => [created, ...current]);
      setSubject(""); setBlocks([paragraph()]); setAttempted(false); setTab("history");
      onCreated();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("support.submitError"));
    } finally { setBusy(false); }
  }

  async function loadMore() {
    if (!nextCursor) return;
    setBusy(true); setError("");
    try {
      const page = await api.mySupportInquiries(nextCursor);
      setItems((current) => [...current, ...page.items]);
      setNextCursor(page.nextCursor || null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("support.loadError"));
    } finally { setBusy(false); }
  }

  return <ModalFrame title={t("support.title")} eyebrow="HELP CENTER" wide onClose={onClose}>
    <div className="support-tabs" role="tablist" aria-label={t("support.tabsLabel")}>
      <button role="tab" aria-selected={tab === "write"} className={tab === "write" ? "active" : ""} onClick={() => { setTab("write"); setError(""); }}><MessageSquareText size={15} />{t("support.write")}</button>
      <button role="tab" aria-selected={tab === "history"} className={tab === "history" ? "active" : ""} onClick={() => { setTab("history"); setError(""); }}><Inbox size={15} />{t("support.history")}</button>
    </div>

    {tab === "write" ? <form className="modal-form support-form" noValidate onSubmit={(event) => void submit(event)}>
      <p className="support-intro span-2">{t("support.intro")}</p>
      <label className="span-2">{t("support.category")}
        <select value={category} onChange={(event) => setCategory(event.target.value as SupportInquiryCategory)}>
          {categories.map((value) => <option key={value} value={value}>{t(`support.category.${value}`)}</option>)}
        </select>
      </label>
      <label className="span-2"><span className="support-field-label">{t("support.subject")} <em className="required-mark">{t("common.required")}</em></span>
        <input aria-label={t("support.subject")} value={subject} required maxLength={200} placeholder={t("support.subjectPlaceholder")} onChange={(event) => setSubject(event.target.value)} />
      </label>
      <div className="span-2 support-editor-field"><span className="support-field-label">{t("support.content")} <em className="required-mark">{t("common.required")}</em></span>
        <BlockEditor ariaLabel={t("support.content")} value={blocks} onChange={setBlocks} onUploadImage={api.uploadFile} loadImage={api.downloadFile} onError={setError} />
        <small>{category === "BUG_REPORT" ? `${t("support.bugPlaceholder")} ` : ""}{t("support.contentHelp")}</small>
      </div>
      {attempted && <RequirementHint className="span-2" missing={missing} label={t("common.requiredRemaining")} />}
      {error && <div className="error-box span-2">{error}</div>}
      <footer className="span-2"><button type="button" onClick={onClose}>{t("common.close")}</button><button className="primary" disabled={busy}><Send size={14} />{busy ? t("common.processing") : t("support.submit")}</button></footer>
    </form> : <div className="support-history" role="tabpanel">
      {error && <div className="error-box">{error}</div>}
      {loading ? <Loading layout="list" label={t("support.loading")} /> : items.length ? <div className="support-list">{items.map((item) => <article key={item.id}>
        <header><span className={`support-category ${item.category === "BUG_REPORT" ? "bug" : ""}`}>{item.category === "BUG_REPORT" && <Bug size={12} />}{t(`support.category.${item.category}`)}</span><span className={`support-status ${item.status.toLowerCase()}`}>{t(`support.status.${item.status}`)}</span></header>
        <h3>{item.untrustedSubject}</h3>
        {item.blocks?.length ? <BlockRenderer blocks={item.blocks} loadImage={api.downloadFile} /> : <p>{item.untrustedContent}</p>}
        <time dateTime={item.createdAt}>{new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(item.createdAt))}</time>
        {item.reply && <section className="support-reply" aria-label={t("support.reply")}>
          <h4>{t(item.reply.source === "AI" ? "support.replyAi" : "support.reply")}</h4>
          <p>{item.reply.untrustedContent}</p>
          <time dateTime={item.reply.createdAt}>{new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(item.reply.createdAt))}</time>
        </section>}
      </article>)}</div> : <div className="support-empty"><Inbox size={28} /><b>{t("support.empty")}</b><p>{t("support.emptyHelp")}</p></div>}
      {nextCursor && <footer><button className="secondary" disabled={busy} onClick={() => void loadMore()}>{busy ? t("common.processing") : t("support.more")}</button></footer>}
    </div>}
  </ModalFrame>;
}
